// Single unified chess-audio module for the whole platform.
//
// Exposes three tiny WebAudio synthesizers so every board (teacher
// explanation, Load Game, LivePuzzle, CaptureActivityPlayer, etc.) plays a
// consistent, kid-friendly sound cue. All sounds are synthesized locally —
// no binary assets, no external URLs, no autoplay-permission ceremony (the
// calls always happen inside a user-gesture chain such as onClick /
// onPieceDrop). Any WebAudio failure is silently swallowed so an audio
// glitch NEVER blocks, delays or cancels a real move.
//
// Historic entry points (chessUtils.playMoveSound / playCaptureSound and
// victorySound.playVictoryOnce) delegate here so the code path is single —
// this file is the ONLY place a sound is defined.

let _ctx = null;

function _getCtx() {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _ctx = new Ctx();
  }
  // If a previous gesture created the context but the tab suspended it,
  // resume asynchronously (fire-and-forget — never awaited so audio never
  // delays the move loop).
  if (_ctx.state === 'suspended') { try { _ctx.resume(); } catch { /* ignore */ } }
  return _ctx;
}

// Short "tock" — soft triangle sweep 320 Hz → 70 Hz over ~100 ms.
export function playChessMoveSound() {
  try {
    const ctx = _getCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(320, t);
    o.frequency.exponentialRampToValueAtTime(70, t + 0.09);
    g.gain.setValueAtTime(0.28, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + 0.12);
  } catch { /* silent */ }
}

// Ascending 3-note chime E5 → A5 → E6, ~230 ms total. More assertive than
// the plain "tock" so captures feel rewarding without being harsh.
export function playChessCaptureSound() {
  try {
    const ctx = _getCtx(); if (!ctx) return;
    const now = ctx.currentTime;
    const notes = [
      { f: 660,  t: now,        d: 0.09 },
      { f: 880,  t: now + 0.07, d: 0.10 },
      { f: 1320, t: now + 0.15, d: 0.14 },
    ];
    for (const n of notes) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(n.f, n.t);
      g.gain.setValueAtTime(0, n.t);
      g.gain.linearRampToValueAtTime(0.18, n.t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, n.t + n.d);
      o.connect(g); g.connect(ctx.destination);
      o.start(n.t); o.stop(n.t + n.d + 0.02);
    }
  } catch { /* silent */ }
}

// Cheerful "coin collect" — bright triangle arpeggio G5-C6-E6 with fast
// staccato attack, ~180 ms total. Distinct from the plain chess capture
// chime: it's higher-pitched and more playful so kids feel a small reward
// every time a Capture Activity target is picked up.
export function playCaptureActivityCollectSound() {
  try {
    const ctx = _getCtx(); if (!ctx) return;
    const now = ctx.currentTime;
    const notes = [
      { f: 783.99,  t: now,        d: 0.09 }, // G5
      { f: 1046.50, t: now + 0.05, d: 0.10 }, // C6
      { f: 1318.51, t: now + 0.10, d: 0.14 }, // E6
    ];
    for (const n of notes) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'triangle';
      o.frequency.setValueAtTime(n.f, n.t);
      g.gain.setValueAtTime(0.0001, n.t);
      g.gain.exponentialRampToValueAtTime(0.20, n.t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, n.t + n.d);
      o.connect(g); g.connect(ctx.destination);
      o.start(n.t); o.stop(n.t + n.d + 0.02);
    }
  } catch { /* silent */ }
}

// Extra-sparkly variant for the LAST target picked up in a Capture Activity.
// Same shape as the regular collect chime but longer and with a top B6
// sparkle — signals "you got the last one!" without stealing the show from
// the immediately following victory arpeggio (still played via
// playChessVictoryOnce, which is deduped per activity).
export function playCaptureActivityFinalCollectSound() {
  try {
    const ctx = _getCtx(); if (!ctx) return;
    const now = ctx.currentTime;
    const notes = [
      { f: 783.99,  t: now,        d: 0.09 }, // G5
      { f: 1046.50, t: now + 0.06, d: 0.10 }, // C6
      { f: 1318.51, t: now + 0.13, d: 0.10 }, // E6
      { f: 1567.98, t: now + 0.20, d: 0.11 }, // G6
      { f: 1975.53, t: now + 0.28, d: 0.16 }, // B6 sparkle
    ];
    for (const n of notes) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'triangle';
      o.frequency.setValueAtTime(n.f, n.t);
      g.gain.setValueAtTime(0.0001, n.t);
      g.gain.exponentialRampToValueAtTime(0.22, n.t + 0.010);
      g.gain.exponentialRampToValueAtTime(0.0001, n.t + n.d);
      o.connect(g); g.connect(ctx.destination);
      o.start(n.t); o.stop(n.t + n.d + 0.02);
    }
  } catch { /* silent */ }
}

// Major arpeggio C5-E5-G5-C6 (~0.55 s) with per-key dedup. Keys the caller
// gives us (e.g. `puzzle:<id>`, `capture:<activity_id>`) prevent the chime
// from ringing twice if React re-renders / polling refreshes fire the
// success confirmation again for the same success.
const _victoryPlayed = new Set();
export function playChessVictoryOnce(key) {
  if (!key) return;
  if (_victoryPlayed.has(key)) return;
  _victoryPlayed.add(key);
  try {
    const ctx = _getCtx(); if (!ctx) return;
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    const step = 0.11;
    const dur  = 0.18;
    notes.forEach((f, i) => {
      const t0 = now + i * step;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'triangle';
      o.frequency.setValueAtTime(f, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      o.connect(g).connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + dur + 0.02);
    });
  } catch { /* silent */ }
}

// Test-only hook — clears the victory dedup set. Not used in production.
export function _resetChessSoundsDedupForTests() { _victoryPlayed.clear(); }
